import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { green, lightBlue } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DateField } from '@mui/x-date-pickers';
import AHDate from "_shared/dates/date";
import React from 'react';
import { diffWords } from 'diff';
import { NumberFormatValues, NumericFormat, SourceInfo } from 'react-number-format';
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';
import TextChange from 'work/proposals/redlining/__diff/text-change';

const PopoverContainer = styled('div')(({ theme }) => ({
  minWidth: '10rem',
  padding: theme.spacing(1.5)
}));
const ChangesPopover = styled(Popover)(() => ({
  pointerEvents: 'none',
  '& .MuiPaper-root': {
    pointerEvents: 'auto'
  }
}));
const ComparisonContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));
const ChangedText = styled(Typography)(({ theme }) => ({
  color: lightBlue[500],
  fontSize: '0.9em',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '1em',
  textAlign: 'center',
  whiteSpace: 'pre-wrap',
  width: '100%'
}));
const LiveDiffText = styled('span')(() => ({
  whiteSpace: 'pre-wrap'
}));
const LiveDiffSegment = styled('span', {
  shouldForwardProp: (prop) => !['added', 'removed'].includes(prop.toString()),
})<{
  added?: boolean;
  removed?: boolean;
}>(({ theme, added, removed }) => ({
  backgroundColor: added ? green[300] : 'transparent',
  textDecoration: removed ? 'line-through' : 'none',
  textDecorationColor: removed ? theme.palette.error.main : 'initial',
  textDecorationThickness: '2px',
}));
const ButtonContainer = styled('div')(() => ({
  width: '100%'
}));
const EditContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(1)
}));

type DiffChangePopoverProps = {
  className?: string;
  anchorElement: Element | null;
  change: TextChange;
  valueType?: 'text' | 'date' | 'percent';
  disableEdit?: boolean;
  onClosePopover: () => void;
  onEditingChange?: (isEditing: boolean) => void;
  onEdit?: (change: TextChange, newValue: string) => void;
  onAccept?: (change: TextChange) => void;
  onReject?: (change: TextChange) => void;
}

function getPercentNumber(value: string | undefined) {
  return value === undefined ? undefined : Number(value) * 100;
}

function getDiffSegmentType(part: { added?: boolean; removed?: boolean }) {
  if (part.added) return 'added';
  if (part.removed) return 'removed';
  return 'same';
}

export default function DiffChangePopover(props: Readonly<DiffChangePopoverProps>) {
  const {
    className,
    change,
    valueType,
    disableEdit,
    onClosePopover,
    onEditingChange,
    onAccept,
    onEdit,
    onReject
  } = props;

  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const currentChange = change.isModified ?
  change?.nextChange?.diff.value : change?.diff.value;
  const [localPopoverState, setLocalPopoverState] = React.useState(() => ({
    sourceAnchorElement: props.anchorElement,
    sourceChange: currentChange,
    sourceValueType: valueType,
    anchorElement: props.anchorElement,
    currentText: currentChange ?? '',
    currentNumber: valueType === 'percent' ? getPercentNumber(currentChange) : undefined as number | undefined,
  }));
  const isCurrentSource =
    localPopoverState.sourceAnchorElement === props.anchorElement &&
    localPopoverState.sourceChange === currentChange &&
    localPopoverState.sourceValueType === valueType;
  const anchorElement = isCurrentSource ? localPopoverState.anchorElement : props.anchorElement;
  const currentText = isCurrentSource ? localPopoverState.currentText : (currentChange ?? '');
  const fallbackCurrentNumber = valueType === 'percent' ? getPercentNumber(currentChange) : undefined;
  const currentNumber = isCurrentSource
    ? localPopoverState.currentNumber
    : fallbackCurrentNumber;

  const currentDisplayValue = React.useMemo(() => {
    if (!isEditing) {
      if (valueType === 'percent') {
        return `${Number(Number.parseFloat(currentChange ?? '0') * 100).toFixed(2)}%`;
      }
      return currentChange ?? '';
    }

    if (valueType === 'percent') {
      return currentNumber == null ? '' : `${currentNumber.toFixed(2)}%`;
    }

    return currentText;
  }, [currentChange, currentNumber, currentText, isEditing, valueType]);

  const currentDisplayNode = React.useMemo(() => {
    if (valueType === 'percent' || valueType === 'date' || !isEditing || currentText === currentChange) {
      return currentDisplayValue;
    }

    return (
      <LiveDiffText>
        {diffWords(currentChange ?? '', currentText).map((part, index) => (
          <LiveDiffSegment
            key={`${part.value}_${getDiffSegmentType(part)}_${index}`}
            added={part.added}
            removed={part.removed}
          >
            {part.value}
          </LiveDiffSegment>
        ))}
      </LiveDiffText>
    );
  }, [currentChange, currentDisplayValue, currentText, isEditing, valueType]);

  const handleDiffPopoverClose = React.useCallback(() =>  {
    setLocalPopoverState({
      sourceAnchorElement: props.anchorElement,
      sourceChange: currentChange,
      sourceValueType: valueType,
      anchorElement: null,
      currentText: currentChange ?? '',
      currentNumber: valueType === 'percent' ? Number(currentChange) * 100 : undefined,
    });
    setIsEditing(false);
  }, [currentChange, props.anchorElement, valueType]);

  React.useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  React.useEffect(() => {
    return () => onEditingChange?.(false);
  }, [onEditingChange]);

  function handleCancelEdit(event: React.MouseEvent | React.KeyboardEvent) {
    event.stopPropagation();
    setIsEditing(false);
    setLocalPopoverState({
      sourceAnchorElement: props.anchorElement,
      sourceChange: currentChange,
      sourceValueType: valueType,
      anchorElement,
      currentText: currentChange ?? '',
      currentNumber: valueType === 'percent' ? Number(currentChange) * 100 : undefined,
    });
  }

  function handleEdit(event: React.MouseEvent | React.KeyboardEvent) {
    event.stopPropagation();

    if (!change) {
      console.error('change is null');
      return;
    }
    if (isEditing) {
      handleDiffPopoverClose();
      onEdit?.(change, valueType === 'percent' ? currentNumber?.toString() ?? '' : currentText);
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }

  return (
    change &&
    <ChangesPopover
      id='changes-popover'
      className={className}
      open={Boolean(anchorElement)}
      anchorReference="anchorEl"
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      slotProps={{
        paper: {
          onMouseLeave: () => {
            if (isEditing) return;
            onClosePopover();
          }
        }
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <PopoverContainer>
        <ComparisonContainer>
          {change.isModified && (
            <>
              <div>
                <Typography variant='overline'>was:</Typography>
                <ChangedText>
                  {valueType === 'percent' ?
                    `${Number(Number.parseFloat(change.diff.value) * 100).toFixed(2)}%` :
                    change.diff.value}
                </ChangedText>
              </div>
              <div>
                <Typography variant='overline'>now:</Typography>
                <ChangedText>
                  {currentDisplayNode}
                </ChangedText>
              </div>
            </>
          )}
          {!change.isModified && change.isAdded && (
            <div>
              <Typography variant='overline'>added:</Typography>
              <ChangedText>{currentDisplayNode}</ChangedText>
            </div>
          )}
          {!change.isModified && change.isRemoved && (
            <div>
              <Typography variant='overline'>removed:</Typography>
              <ChangedText>{change.diff.value}</ChangedText>
            </div>
          )}
        </ComparisonContainer>
        {isEditing && (
          <EditContainer>
            {(!valueType || valueType === 'text') && (
              <TextField
                variant='standard'
                size='small'
                label='New text'
                value={currentText}
                onChange={(event) => setLocalPopoverState((currentState) => ({
                  ...currentState,
                  sourceAnchorElement: props.anchorElement,
                  sourceChange: currentChange,
                  sourceValueType: valueType,
                  currentText: event.target.value,
                }))}
                onKeyDown={(event) => {
                  if (event.key === KeyboardKey.Enter && currentText !== '') {
                    handleEdit(event);
                  }
                  if (event.key === KeyboardKey.Escape) {
                    handleCancelEdit(event);
                  }
                }}
              />
            )}
            {valueType === 'date' && (
              <DateField
                variant='standard'
                size='small'
                label='new AHDate'
                value={new AHDate(currentText).asMoment}
                onChange={(date) => setLocalPopoverState((currentState) => ({
                  ...currentState,
                  sourceAnchorElement: props.anchorElement,
                  sourceChange: currentChange,
                  sourceValueType: valueType,
                  currentText: date?.format('MM/DD/YY') ?? '',
                }))}
                onKeyDown={(event) => {
                  if (event.key === KeyboardKey.Enter && currentText !== '') {
                    handleEdit(event);
                  }
                  if (event.key === KeyboardKey.Escape) {
                    handleCancelEdit(event);
                  }
                }}
              />
            )}
            {valueType === 'percent' && (
              <NumericFormat
                placeholder="New percentage"
                variant="standard"
                size='small'
                customInput={TextField}
                value={currentNumber}
                suffix='%'
                type='text'
                thousandSeparator={false}
                allowNegative={false}
                decimalScale={2}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return floatValue == null || (floatValue >= 0 && floatValue <= 100);
                }}
                onKeyDown={(event) => {
                  if (event.key === KeyboardKey.Enter && currentText !== '') {
                    handleEdit(event);
                  }
                  if (event.key === KeyboardKey.Escape) {
                    handleCancelEdit(event);
                  }
                }}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) => {
                  if (sourceInfo.source === 'prop') return;
                  setLocalPopoverState((currentState) => ({
                    ...currentState,
                    sourceAnchorElement: props.anchorElement,
                    sourceChange: currentChange,
                    sourceValueType: valueType,
                    currentNumber: values.floatValue,
                  }));
                }}
              />
            )}
          </EditContainer>
        )}
        <ButtonContainer>
          {!disableEdit && (
            <Button
              startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
              size='small'
              disabled={currentText === ''}
              onClick={(event) => handleEdit(event)}
            >
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          )}
        </ButtonContainer>
        {isEditing && (
          <ButtonContainer>
            <Button
              startIcon={<CloseIcon />}
              size='small'
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
          </ButtonContainer>
        )}
        {!isEditing && (
          <>
            <ButtonContainer>
              <Button
                startIcon={<CheckIcon color="success" />}
                size='small'
                onClick={(event) => {
                  event.stopPropagation();
                  handleDiffPopoverClose();
                  if (!change) {
                    console.error('changes is null');
                    return;
                  }
                  onAccept?.(change);
                }}
              >
                Accept
              </Button>
            </ButtonContainer>
            <ButtonContainer>
              <Button
                startIcon={<CloseIcon color="error" />}
                size='small'
                onClick={(event) => {
                  event.stopPropagation();
                  handleDiffPopoverClose();
                  if (!change) {
                    console.error('change is null');
                    return;
                  }
                  onReject?.(change);
                }}
              >
                Reject
              </Button>
            </ButtonContainer>
          </>
        )}
      </PopoverContainer>
    </ChangesPopover>
  )
}
